import { useEffect, useState, useCallback } from "react";
import { Search, ShoppingCart, CheckCircle2, XCircle, BadgeAlert } from "lucide-react";
import { toast } from "sonner";
import api, { fmtINR, fmtDateTime, errMsg } from "../../lib/api";
import { PageHeader, StatusBadge, TableShell, TR, TD, LoadingRows, EmptyState } from "../../components/Shared";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const STATUSES = ["pending", "confirmed", "shipped", "delivered", "cancelled"];
const PAY_FILTERS = [
  { key: "", label: "All" },
  { key: "pending_verification", label: "Pending Verification" },
  { key: "paid", label: "Confirmed" },
  { key: "cancelled", label: "Cancelled" },
];

export default function StoreOrders() {
  const [orders, setOrders] = useState(null);
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("");
  const [payStatus, setPayStatus] = useState("");
  const [selected, setSelected] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => {
    api.get("/store-admin/orders", { params: { q, status, payment_status: payStatus } }).then((r) => setOrders(r.data));
  }, [q, status, payStatus]);
  useEffect(() => { load(); }, [load]);

  const updateStatus = async (oid, newStatus) => {
    try {
      const { data } = await api.patch(`/store-admin/orders/${oid}`, { status: newStatus });
      toast.success(`Order ${data.order_number} → ${newStatus}`);
      setSelected(data); load();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const verifyPayment = async (oid, action) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/store-admin/orders/${oid}/payment/${action}`);
      toast.success(action === "confirm" ? `Payment confirmed — ${data.order_number} marked Paid` : `Payment cancelled for ${data.order_number}`);
      setSelected(data); load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="store-orders">
      <PageHeader title="Orders" subtitle="Track and fulfil customer orders." />
      <div className="flex flex-wrap gap-3 mb-6">
        <div className="relative max-w-sm flex-1 min-w-52">
          <Search className="h-4 w-4 absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input data-testid="order-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search order # or customer…" className="pl-10 h-11 rounded-full" />
        </div>
        <div className="flex gap-2 flex-wrap">
          {["", ...STATUSES].map((s) => (
            <button key={s} data-testid={`order-filter-${s || "all"}`} onClick={() => setStatus(s)}
              className={`px-4 h-11 rounded-full text-sm font-bold border capitalize transition-colors ${status === s ? "bg-primary text-primary-foreground border-transparent" : "border-border"}`}>
              {s || "All"}
            </button>
          ))}
        </div>
        <div className="flex gap-2 flex-wrap" data-testid="payment-filters">
          {PAY_FILTERS.map((f) => (
            <button key={f.key} data-testid={`pay-filter-${f.key || "all"}`} onClick={() => setPayStatus(f.key)}
              className={`px-4 h-11 rounded-full text-sm font-bold border transition-colors ${payStatus === f.key ? "bg-amber-500 text-white border-transparent" : "border-border"}`}>
              {f.label}
            </button>
          ))}
        </div>
      </div>

      {!orders ? <LoadingRows /> : orders.length === 0 ? (
        <EmptyState icon={ShoppingCart} title="No orders" body="Orders appear here as customers check out." />
      ) : (
        <TableShell testid="orders-table" head={["Order", "Customer", "Items", "Total", "Payment", "Status", "Placed"]}>
          {orders.map((o) => (
            <TR key={o.id} testid={`order-row-${o.id}`} onClick={() => setSelected(o)}>
              <TD className="font-mono text-xs font-bold">{o.order_number}</TD>
              <TD><p className="text-sm font-bold">{o.customer?.name}</p><p className="text-xs text-muted-foreground">{o.customer?.phone}</p></TD>
              <TD className="text-sm">{o.items?.reduce((s, i) => s + i.qty, 0)}</TD>
              <TD className="font-bold">{fmtINR(o.total)}</TD>
              <TD>
                <div className="flex items-center gap-2">
                  <StatusBadge status={o.payment_status} />
                  {o.payment_status === "pending_verification" && (
                    <span data-testid={`pending-indicator-${o.id}`} title="Manual UPI — verify payment in your UPI app before confirming"
                      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-500/15 text-amber-600 text-[10px] font-extrabold animate-pulse">
                      <BadgeAlert className="h-3 w-3" /> UPI CHECK
                    </span>
                  )}
                </div>
              </TD>
              <TD><StatusBadge status={o.status} /></TD>
              <TD className="text-xs text-muted-foreground">{fmtDateTime(o.created_at)}</TD>
            </TR>
          ))}
        </TableShell>
      )}

      <Dialog open={!!selected} onOpenChange={() => setSelected(null)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader><DialogTitle className="font-display text-2xl">Order {selected?.order_number}</DialogTitle></DialogHeader>
          {selected && (
            <div className="space-y-5 mt-2" data-testid="order-detail">
              <div className="text-sm space-y-1">
                <p className="font-bold">{selected.customer?.name}</p>
                <p className="text-muted-foreground">{selected.customer?.phone} · {selected.customer?.email}</p>
                <p className="text-muted-foreground">{selected.customer?.address}</p>
              </div>
              <div className="border border-border rounded-md divide-y divide-border">
                {selected.items?.map((it, i) => (
                  <div key={i} className="flex items-center gap-3 px-4 py-3">
                    <div className="h-10 w-10 rounded bg-muted overflow-hidden shrink-0">{it.image && <img src={it.image} alt="" className="h-full w-full object-cover" />}</div>
                    <div className="flex-1"><p className="text-sm font-bold">{it.name}</p><p className="text-xs text-muted-foreground">×{it.qty}</p></div>
                    <span className="text-sm font-bold">{fmtINR(it.price * it.qty)}</span>
                  </div>
                ))}
                <div className="px-4 py-3 text-xs text-muted-foreground space-y-1">
                  <div className="flex justify-between"><span>Subtotal</span><span>{fmtINR(selected.subtotal)}</span></div>
                  {selected.discount > 0 && <div className="flex justify-between text-emerald-600"><span>Discount {selected.coupon && `(${selected.coupon})`}</span><span>-{fmtINR(selected.discount)}</span></div>}
                  <div className="flex justify-between"><span>Shipping</span><span>{selected.shipping ? fmtINR(selected.shipping) : "Free"}</span></div>
                  <div className="flex justify-between"><span>Tax</span><span>{fmtINR(selected.tax)}</span></div>
                  <div className="flex justify-between font-display font-extrabold text-base text-foreground pt-1"><span>Total</span><span>{fmtINR(selected.total)}</span></div>
                </div>
              </div>
              {selected.payment_method === "upi" && (
                <div className="border border-amber-500/30 bg-amber-500/5 rounded-md p-4 space-y-3" data-testid="upi-verify-section">
                  <div className="flex items-center justify-between gap-3 flex-wrap">
                    <div>
                      <p className="text-sm font-extrabold flex items-center gap-1.5"><BadgeAlert className="h-4 w-4 text-amber-600" /> Manual UPI Payment</p>
                      <p className="text-xs text-muted-foreground">UPI ID used: <span className="font-mono font-bold">{selected.upi_id || "—"}</span></p>
                      {selected.payment_verified_by && <p className="text-xs text-emerald-600 font-bold mt-1">Confirmed by {selected.payment_verified_by} · {fmtDateTime(selected.payment_verified_at)}</p>}
                      {selected.payment_cancelled_by && <p className="text-xs text-rose-500 font-bold mt-1">Cancelled by {selected.payment_cancelled_by} · {fmtDateTime(selected.payment_cancelled_at)}</p>}
                    </div>
                    <StatusBadge status={selected.payment_status} />
                  </div>
                  {selected.payment_status === "pending_verification" && (
                    <>
                      <p className="text-xs text-amber-700 font-semibold">Check your PhonePe / Google Pay / UPI app for {fmtINR(selected.total)} before confirming.</p>
                      <div className="flex gap-2 flex-wrap">
                        <button data-testid="upi-confirm-button" disabled={busy} onClick={() => verifyPayment(selected.id, "confirm")}
                          className="flex items-center gap-1.5 px-4 h-10 rounded-full text-xs font-extrabold bg-emerald-600 text-white hover:bg-emerald-700 transition-colors disabled:opacity-50">
                          <CheckCircle2 className="h-4 w-4" /> Confirm Payment
                        </button>
                        <button data-testid="upi-cancel-button" disabled={busy} onClick={() => verifyPayment(selected.id, "cancel")}
                          className="flex items-center gap-1.5 px-4 h-10 rounded-full text-xs font-extrabold bg-rose-500 text-white hover:bg-rose-600 transition-colors disabled:opacity-50">
                          <XCircle className="h-4 w-4" /> Cancel Payment
                        </button>
                        <span className="text-xs text-muted-foreground self-center font-semibold">or keep it Pending Verification</span>
                      </div>
                    </>
                  )}
                </div>
              )}
              <div className="flex items-center gap-3">
                <span className="overline-label">Update status</span>
                <Select value={selected.status} onValueChange={(v) => updateStatus(selected.id, v)}>
                  <SelectTrigger data-testid="order-status-select" className="w-44 rounded-full"><SelectValue /></SelectTrigger>
                  <SelectContent>{STATUSES.map((s) => <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>)}</SelectContent>
                </Select>
                <StatusBadge status={selected.payment_status} />
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
